// src/components/PublicNavbar.tsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { GlobalStyles } from "../styles/globalStyles";
import { Menu, X, Moon, Sun, Sparkles } from "lucide-react";
import logo from "../assets/images/logo.png";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { toggleTheme, setTheme } from "../redux/slices/themeSlice";

interface PublicNavbarProps {
  currentPage?: string;
}

const PublicNavbar: React.FC<PublicNavbarProps> = ({ currentPage }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const dispatch = useDispatch();
  const { mode } = useSelector((state: RootState) => state.theme);

  // Sync with Redux on initial load
  useEffect(() => {
    const storedTheme = localStorage.getItem("theme");
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;

    if (storedTheme === "dark" || (!storedTheme && prefersDark)) {
      dispatch(setTheme("dark"));
    } else {
      dispatch(setTheme("light"));
    }
  }, [dispatch]);

  const toggleDarkMode = () => {
    dispatch(toggleTheme());
  };

  const navigationItems = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Privacy", path: "/privacy" },
    { name: "Terms", path: "/terms" },
  ];

  return (
    <nav className="border-b border-gray-200/30 dark:border-gray-800/30 bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl sticky top-0 z-50">
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex h-16 items-center justify-center border-b border-gray-200/30 dark:border-gray-700/30">
            <Link to="/" className="flex items-center gap-2 group/logo  ">
              <img
                src={logo}
                alt="ObeliskRecovery"
                className="h-14 w-auto transition-transform group-hover/logo:scale-105"
              />
              <span className={`${GlobalStyles.heading3} text-primary`}>
                Syvron Recovery
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`text-sm px-3 py-1.5 rounded-full transition-all duration-300 group/nav ${
                  currentPage === item.path
                    ? "bg-gradient-to-r from-primary/10 to-primary/5 text-primary font-medium border border-primary/20"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 hover:text-primary"
                }`}
              >
                <span className="relative">
                  {item.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary group-hover/nav:w-full transition-all duration-300"></span>
                </span>
              </Link>
            ))}
            <div className="flex items-center space-x-2 ml-4">
              {/* Dark Mode Toggle Button */}
              <button
                onClick={toggleDarkMode}
                className={`${GlobalStyles.iconButton} hover:bg-primary/10`}
                aria-label={
                  mode === "dark"
                    ? "Switch to light mode"
                    : "Switch to dark mode"
                }
              >
                {mode === "dark" ? (
                  <Sun className="h-5 w-5 transition-transform hover:rotate-12" />
                ) : (
                  <Moon className="h-5 w-5 transition-transform hover:rotate-12" />
                )}
              </button>

              <Link
                to="/login"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonSmall} group/login`}
              >
                <span>Login</span>
                <svg
                  className="w-4 h-4 ml-1 opacity-0 group-hover/login:opacity-100 transition-opacity"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                  />
                </svg>
              </Link>
              <Link
                to="/register"
                className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonSmall} group/register`}
              >
                <span>Get Started</span>
                <Sparkles className="w-4 h-4 ml-1 opacity-0 group-hover/register:opacity-100 transition-opacity" />
              </Link>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-2">
            {/* Dark Mode Toggle Button (Mobile Header) */}
            <button
              onClick={toggleDarkMode}
              className={`${GlobalStyles.iconButton} hover:bg-primary/10`}
              aria-label={
                mode === "dark" ? "Switch to light mode" : "Switch to dark mode"
              }
            >
              {mode === "dark" ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </button>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`${GlobalStyles.iconButton} hover:bg-primary/10`}
            >
              {isMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden mt-3 pb-3 border-t border-gray-200/30 dark:border-gray-800/30 pt-3 backdrop-blur-xl">
            <div className="space-y-2">
              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-4 py-2.5 rounded-lg transition-all duration-300 group/mobile ${
                    currentPage === item.path
                      ? "bg-gradient-to-r from-primary/10 to-primary/5 text-primary font-medium"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-800/50 hover:text-primary"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{item.name}</span>
                    <Sparkles className="h-3 w-3 opacity-0 group-hover/mobile:opacity-100 transition-opacity" />
                  </div>
                </Link>
              ))}
              <div className="pt-3 space-y-2">
                <Link
                  to="/login"
                  onClick={() => setIsMenuOpen(false)}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonMedium} ${GlobalStyles.buttonOutline} w-full justify-center group/login-mobile`}
                >
                  <span>Login</span>
                  <svg
                    className="w-4 h-4 ml-2 opacity-0 group-hover/login-mobile:opacity-100 transition-opacity"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                    />
                  </svg>
                </Link>
                <Link
                  to="/register"
                  onClick={() => setIsMenuOpen(false)}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonMedium} ${GlobalStyles.buttonPrimary} w-full justify-center group/register-mobile`}
                >
                  <span>Get Started</span>
                  <Sparkles className="w-4 h-4 ml-2 opacity-0 group-hover/register-mobile:opacity-100 transition-opacity" />
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default PublicNavbar;
